import { shipSystemIdSchema } from "./ship-systems.js";
import { z } from "zod";
export const SEASON = {
  id: "frontier-2026-01",
  version: "season-20261027-v3",
  name: "Первый сигнал",
  startsAt: Date.UTC(2026, 9, 27),
  endsAt: Date.UTC(2026, 11, 22),
  levels: 40,
  xpPerLevel: 400,
} as const;
export const SEASON_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
export const seasonActivitySchema = z.enum([
  "combat",
  "mining",
  "contract",
  "event",
]);
export type SeasonActivity = z.infer<typeof seasonActivitySchema>;
export const seasonTaskSchema = z.strictObject({
  format: z.literal(1),
  id: z.string().regex(/^week-[1-8]-(combat|mining|contract|event)$/),
  week: z.number().int().min(1).max(8),
  title: z.string().min(1).max(80),
  activity: seasonActivitySchema,
  target: z.number().int().min(1).max(1000),
  xp: z.number().int().min(1).max(1000),
  opensAt: z.number().int().nonnegative(),
});
export type SeasonTask = z.infer<typeof seasonTaskSchema>;
export const SEASON_TASKS: readonly SeasonTask[] = Array.from(
  { length: 8 },
  (_, index) => {
    const week = index + 1;
    const opensAt = SEASON.startsAt + index * SEASON_WEEK_MS;
    const shared = { format: 1 as const, week, opensAt };
    return [
      {
        ...shared,
        id: `week-${week}-combat`,
        title: "Подавить угрозу: уничтожить противников",
        activity: "combat" as const,
        target: 20 + index * 5,
        xp: 150,
      },
      {
        ...shared,
        id: `week-${week}-mining`,
        title: "Разработка астероидов",
        activity: "mining" as const,
        target: 12 + index * 3,
        xp: 100,
      },
      index % 2 === 0
        ? {
            ...shared,
            id: `week-${week}-contract`,
            title: "Выполнить обычные контракты",
            activity: "contract" as const,
            target: 3 + Math.floor(index / 2),
            xp: 150,
          }
        : {
            ...shared,
            id: `week-${week}-event`,
            title: "Успешно помочь в общих событиях",
            activity: "event" as const,
            target: 1 + Math.floor(index / 2),
            xp: 150,
          },
    ];
  },
).flat();
export const seasonTaskViewSchema = seasonTaskSchema.extend({
  progress: z.number().int().nonnegative().max(1000),
  completed: z.boolean(),
});
export const PAINT_IDS = [
  "default",
  "azure",
  "amber",
  "violet",
  "signal",
  "nova",
] as const;
export const paintIdSchema = z.enum(PAINT_IDS);
export type PaintId = z.infer<typeof paintIdSchema>;
export const PAINTS: Record<PaintId, { name: string; tint: number }> = {
  default: { name: "Заводское", tint: 0xffffff },
  azure: { name: "Лазурь", tint: 0x83d8ff },
  amber: { name: "Янтарь", tint: 0xffcc73 },
  violet: { name: "Туманность", tint: 0xd3a4ff },
  signal: { name: "Первый сигнал", tint: 0x86ffcf },
  nova: { name: "Сверхновая", tint: 0xff95d6 },
};
export const SEASON_MODULE_LEVEL = 8;
export const seasonEquipmentRewardSchema = z.strictObject({
  kind: z.literal("system"),
  id: shipSystemIdSchema,
  tier: z.union([z.literal(0), z.literal(1), z.literal(2)]),
});
export type SeasonEquipmentReward = z.infer<typeof seasonEquipmentRewardSchema>;
export const seasonRewardSchema = z.strictObject({
  level: z.number().int().min(1).max(SEASON.levels),
  premiumOnly: z.boolean(),
  credits: z.string().regex(/^[1-9]\d*$/),
  components: z.number().int().positive(),
  paint: paintIdSchema.nullable(),
  equipment: seasonEquipmentRewardSchema.nullable(),
});
export type SeasonReward = z.infer<typeof seasonRewardSchema>;

/** The dated ordinary recipe survives independently of future season catalogs. */
export function ordinarySeasonBlueprints(
  now: number,
): { kind: "system"; id: "reactor" }[] {
  return now >= Date.UTC(2026, 11, 22)
    ? [{ kind: "system", id: "reactor" }]
    : [];
}

export const SEASON_UNLOCK_VERSION = "season-unlock-20261027-v2";
export const SEASON_UNLOCK_COST = {
  credits: "1500000",
  components: 45000,
} as const;
export const SEASON_FREE_BUDGET = {
  credits: "200000",
  components: 6000,
} as const;
const freeLevels = Array.from(
  { length: SEASON.levels },
  (_, index) => index + 1,
).filter((level) => level % 5 !== 0);
const paidLevels = Array.from(
  { length: SEASON.levels / 5 },
  (_, index) => (index + 1) * 5,
);

/** Cumulative integer allocation keeps the exact budget without losing rounding remainders. */
function allocate(
  total: bigint,
  levels: readonly number[],
  weight: (level: number) => number,
) {
  const denominator = BigInt(
    levels.reduce((sum, level) => sum + weight(level), 0),
  );
  let cumulative = 0n,
    issued = 0n;
  const result = new Map<number, bigint>();
  for (const level of levels) {
    cumulative += BigInt(weight(level));
    const next = (total * cumulative) / denominator;
    result.set(level, next - issued);
    issued = next;
  }
  return result;
}
const freeWeight = (level: number) => 4 + level;
const paidWeight = (level: number) => (level / 5) ** 2;
const freeCredits = allocate(
  BigInt(SEASON_FREE_BUDGET.credits),
  freeLevels,
  freeWeight,
);
const freeComponents = allocate(
  BigInt(SEASON_FREE_BUDGET.components),
  freeLevels,
  freeWeight,
);
const paidCredits = allocate(
  BigInt(SEASON_UNLOCK_COST.credits) * 2n,
  paidLevels,
  paidWeight,
);
const paidComponents = allocate(
  BigInt(SEASON_UNLOCK_COST.components) * 2n,
  paidLevels,
  paidWeight,
);
export const SEASON_REWARDS: readonly SeasonReward[] = Array.from(
  { length: SEASON.levels },
  (_, index) => {
    const level = index + 1,
      premiumOnly = level % 5 === 0;
    const credits = (premiumOnly ? paidCredits : freeCredits).get(level);
    const components = (premiumOnly ? paidComponents : freeComponents).get(
      level,
    );
    if (credits === undefined || components === undefined)
      throw new Error("Missing season budget allocation");
    return seasonRewardSchema.parse({
      level,
      premiumOnly,
      credits: credits.toString(),
      components: Number(components),
      equipment:
        level === SEASON_MODULE_LEVEL
          ? { kind: "system", id: "reactor", tier: 0 }
          : null,
      paint:
        level === 10
          ? "violet"
          : level === 12
            ? "azure"
            : level === 20
              ? "nova"
              : level === 24
                ? "amber"
                : level === 40
                  ? "signal"
                  : null,
    });
  },
);
export const seasonUnlockPriceSchema = z.strictObject({
  version: z.string().min(1).max(64),
  credits: z.string().regex(/^[1-9]\d*$/),
  components: z.number().int().positive(),
  paidCredits: z.string().regex(/^[1-9]\d*$/),
  paidComponents: z.number().int().positive(),
});
export function currentSeasonPrice() {
  const paid = SEASON_REWARDS.filter((reward) => reward.premiumOnly);
  return seasonUnlockPriceSchema.parse({
    version: SEASON_UNLOCK_VERSION,
    ...SEASON_UNLOCK_COST,
    paidCredits: paid
      .reduce((sum, reward) => sum + BigInt(reward.credits), 0n)
      .toString(),
    paidComponents: paid.reduce((sum, reward) => sum + reward.components, 0),
  });
}
export const seasonPassTermsSchema = z.strictObject({
  format: z.literal(1),
  seasonId: z.string().min(1).max(100),
  version: z.string().min(1).max(100),
  price: seasonUnlockPriceSchema,
  rewards: z.array(seasonRewardSchema).length(SEASON.levels),
});
export type SeasonPassTerms = z.infer<typeof seasonPassTermsSchema>;
export function captureSeasonPassTerms(): SeasonPassTerms {
  return decodeSeasonPassTerms({
    format: 1,
    seasonId: SEASON.id,
    version: SEASON.version,
    price: currentSeasonPrice(),
    rewards: [...structuredClone(SEASON_REWARDS)],
  });
}
export function decodeSeasonPassTerms(value: SeasonPassTerms): SeasonPassTerms {
  const terms = seasonPassTermsSchema.parse(value);
  const paid = terms.rewards.filter((reward) => reward.premiumOnly);
  const credits = paid.reduce(
    (sum, reward) => sum + BigInt(reward.credits),
    0n,
  );
  const components = paid.reduce((sum, reward) => sum + reward.components, 0);
  if (
    new Set(terms.rewards.map((reward) => reward.level)).size !==
      SEASON.levels ||
    credits !== BigInt(terms.price.paidCredits) ||
    components !== terms.price.paidComponents ||
    credits !== BigInt(terms.price.credits) * 2n ||
    components !== terms.price.components * 2
  )
    throw new Error("Invalid frozen season pass terms");
  return terms;
}

export const seasonClaimInputSchema = z.strictObject({
  seasonId: z.literal(SEASON.id),
  level: z.number().int().min(1).max(SEASON.levels),
});
export const seasonViewSchema = z.strictObject({
  id: z.literal(SEASON.id),
  version: z.literal(SEASON.version),
  name: z.string(),
  startsAt: z.number().int(),
  endsAt: z.number().int(),
  serverNow: z.number().int(),
  xp: z.number().int().nonnegative(),
  level: z.number().int().min(0).max(SEASON.levels),
  xpPerLevel: z.number().int().positive(),
  levels: z.literal(40),
  premium: z.boolean(),
  rewards: z
    .array(seasonRewardSchema.extend({ claimed: z.boolean() }))
    .max(SEASON.levels),
  unlockPrice: seasonUnlockPriceSchema.optional(),
  paints: z.array(paintIdSchema).max(PAINT_IDS.length),
  selectedPaint: paintIdSchema,
  tasks: z.array(seasonTaskViewSchema).max(24).optional(),
});
export type SeasonView = z.infer<typeof seasonViewSchema>;
export interface SeasonProof {
  playerId: string;
  runId: string;
  sourceId: string;
  xp: number;
  occurredAt: number;
  activity?: SeasonActivity;
}

export const seasonUnlockInputSchema = z.strictObject({
  requestId: z.string().min(8).max(100),
  seasonId: z.literal(SEASON.id),
  priceVersion: z.string().min(1).max(64),
});
